// ── Site menu ────────────────────────────────────────────────────────────────
// A copy of the the reference store menu layout (owner request 2026-10-18, "I want
// to match the menu the reference store has"), filled with what we sell:
//
//   • The menu bar names PRODUCT TYPES, led by "All Blinds & Shades", plus one
//     niche menu (theirs is No Drill, ours is Total Blackout). Each label is a
//     link to its page; hovering opens its panel.
//   • A panel is a product photo, up to three columns of groups (By Style /
//     By Feature / …), and a "Shop All …" button.
//   • Help, measuring, consultation and company links sit in a "?" dropdown in
//     the header, not in the menu bar.
//
// the matched retailer's Roman, Curtains and Shutters menus have no counterpart because
// we sell none of them; Skylight (owner request 2026-10-17), Motorized and
// Outdoor take those places. Per-type "By Room" columns need a room filter on
// the listing page, which does not exist yet, so rooms live in the
// All Blinds & Shades panel only.
//
// The desktop panels, the mobile drawer and the footer all render from this
// file, so they cannot drift apart.
export interface MenuLink {
  label: string;
  href: string;
  badge?: string;
  // Slug of the product whose photo stands for this link in a collection
  // page's "Explore more" strip. Not needed for /products/<slug> links (that
  // product's photo) or for links to a menu section's own page (its image).
  photoOf?: string;
}

export interface MenuGroup {
  title: string;
  // A group title is itself a link on the reference store; ours too when set.
  href?: string;
  links: MenuLink[];
}

export interface MenuSection {
  key: string;
  label: string;
  // Where the menu-bar label goes when clicked.
  href: string;
  // Square photo at the left of the panel. Upload paths come from the product
  // or category record named in the comment next to each one.
  image?: { src: string; alt: string };
  // Up to three columns; each column stacks one or more groups.
  columns: MenuGroup[][];
  shopAll: MenuLink;
}

export const ROOM_LINKS: MenuLink[] = [
  { label: 'Bedroom', href: '/bedroom-blinds' },
  { label: 'Living Room', href: '/living-room-blinds' },
  { label: 'Kitchen', href: '/kitchen-blinds' },
  { label: 'Bathroom', href: '/bathroom-blinds' },
  { label: 'Nursery', href: '/nursery-blinds' },
  { label: 'Home Office', href: '/home-office-blinds' },
  { label: 'Dining Room', href: '/dining-room-blinds' },
];

export const MENU_SECTIONS: MenuSection[] = [
  {
    key: 'all',
    label: 'All Blinds & Shades',
    href: '/products',
    // Category 1 (Shades) image.
    image: { src: '/uploads/categories/categories_1770691753386_52e29871b2dd1d30.webp', alt: 'Custom roller shades in a living room' },
    columns: [
      [
        {
          title: 'Shades',
          href: '/products',
          links: [
            { label: 'Cellular Shades', href: '/cellular-shades' },
            { label: 'Roller Shades', href: '/roller-shades' },
            { label: 'Solar Shades', href: '/solar-shades', photoOf: '5-percent-openness-solar-shades' },
            { label: 'Zebra Shades', href: '/zebra-blinds', photoOf: 'premium-zebra' },
            { label: 'Sheer Shades', href: '/products/cordless-sheer-shades' },
            { label: 'Pleated Shades', href: '/products/cordless-blackout-pleated-shades' },
          ],
        },
        {
          title: 'By Feature',
          links: [
            { label: 'Blackout', href: '/blackout-blinds' },
            { label: 'Motorized', href: '/motorized-shades' },
            { label: 'Cordless', href: '/cordless-blinds' },
            { label: 'Top Down Bottom Up', href: '/top-down-bottom-up-shades', photoOf: 'top-down-bottom-up-cellular-shades' },
            { label: 'Energy Saving', href: '/energy-efficient-blinds' },
          ],
        },
      ],
      [
        {
          title: 'Blinds',
          href: '/window-blinds',
          links: [
            { label: 'Faux Wood Blinds', href: '/products/motorized-faux-wood-blinds' },
            { label: 'Vertical Blinds', href: '/vertical-blinds', photoOf: 'fabric-vertical-blinds' },
            { label: 'Shop All', href: '/window-blinds' },
          ],
        },
        {
          title: 'By Room',
          href: '/rooms',
          links: [...ROOM_LINKS.slice(0, 4), { label: 'View All', href: '/rooms' }],
        },
      ],
      [
        {
          title: 'Specialty',
          links: [
            { label: 'Skylights', href: '/cellular-shades-for-skylights' },
            { label: 'Outdoor', href: '/outdoor-roller-shades' },
            { label: 'Sunrooms', href: '/sunroom-blinds' },
            { label: 'Sliding Doors', href: '/blinds-for-sliding-doors' },
            { label: 'Large Windows', href: '/blinds-for-large-windows' },
          ],
        },
        {
          // Where the reference store lists brands. Smart-home platform is the
          // equivalent choice a buyer makes here.
          title: 'By Smart Home',
          href: '/smart-home-compatibility',
          links: [
            { label: 'Amazon Alexa', href: '/smart-blinds-alexa' },
            { label: 'Google Home', href: '/smart-blinds-google-home' },
            { label: 'Apple HomeKit', href: '/smart-blinds-homekit' },
          ],
        },
      ],
    ],
    shopAll: { label: 'Shop All Products', href: '/products' },
  },
  {
    key: 'cellular',
    label: 'Cellular Shades',
    href: '/cellular-shades',
    // Product 461 (Single Cell Light Filtering Shades) image.
    image: { src: '/uploads/products/ai-461-8698aae5.webp', alt: 'Cellular shades in a bedroom' },
    columns: [
      [
        {
          title: 'By Style',
          href: '/cellular-shades',
          links: [
            { label: 'Single Cell Light Filtering', href: '/products/single-cell-light-filtering-shades' },
            { label: 'Cordless Honeycomb', href: '/products/26mm-cordless-honeycomb-blind' },
            { label: 'Top Down Bottom Up', href: '/products/top-down-bottom-up-cellular-shades' },
            { label: 'Skylight Shades', href: '/cellular-shades-for-skylights' },
          ],
        },
      ],
      [
        {
          title: 'By Feature',
          href: '/cellular-shades',
          links: [
            { label: 'Blackout', href: '/products/premium-blackout-cellular-shades' },
            { label: 'Motorized', href: '/products/motorized-blackout-cellular-shades' },
            { label: 'Cordless', href: '/cordless-cellular-shades' },
            { label: 'Energy Saving', href: '/best-blinds-for-insulation' },
          ],
        },
      ],
      [
        {
          title: 'Guides',
          links: [
            { label: 'Cellular Shades Buying Guide', href: '/guides/cellular-shades' },
            { label: 'Cellular vs Roller Shades', href: '/cellular-vs-roller-shades' },
            { label: 'Winter Energy Savings', href: '/energy-efficient-winter-shades' },
          ],
        },
      ],
    ],
    shopAll: { label: 'Shop All Cellulars', href: '/cellular-shades' },
  },
  {
    key: 'roller',
    label: 'Roller Shades',
    href: '/roller-shades',
    // Product 418 (Light Filtering Roller Shades) image.
    image: { src: '/uploads/products/ai-418-c2f59ec4.webp', alt: 'Roller shades in a loft' },
    columns: [
      [
        {
          title: 'By Style',
          href: '/roller-shades',
          links: [
            { label: 'Light Filtering', href: '/products/light-filtering-roller-shades' },
            { label: 'Designer Patterns', href: '/products/designer-roller-shades' },
            { label: 'Solar Shades', href: '/solar-shades', photoOf: '5-percent-openness-solar-shades' },
            { label: 'Zebra Shades', href: '/zebra-blinds', photoOf: 'premium-zebra' },
          ],
        },
      ],
      [
        {
          title: 'By Feature',
          href: '/roller-shades',
          links: [
            { label: 'Blackout', href: '/products/blackout-roller-shades' },
            { label: 'Total Blackout Side Channels', href: '/blackout-roller-shades-with-side-channels', photoOf: 'blackout-roller-shades-side-channels' },
            { label: 'Motorized', href: '/products/motorized-roller-shades' },
            { label: 'Outdoor', href: '/outdoor-roller-shades' },
          ],
        },
      ],
      [
        {
          title: 'Guides',
          links: [
            { label: 'Roller Shades Buying Guide', href: '/guides/roller-shades' },
            { label: 'Solar Openness: 1% vs 3% vs 5%', href: '/solar-shade-openness-1-vs-3-vs-5' },
            { label: 'Zebra vs Roller Shades', href: '/zebra-blinds-vs-roller-shades' },
          ],
        },
      ],
    ],
    shopAll: { label: 'Shop All Rollers', href: '/roller-shades' },
  },
  {
    key: 'blinds',
    label: 'Blinds',
    href: '/window-blinds',
    // Product 456 (Motorized Faux Wood Blinds) image.
    image: { src: '/uploads/products/fauxwood-room-456.webp', alt: 'Faux wood blinds in a living room' },
    columns: [
      [
        {
          title: 'By Style',
          href: '/window-blinds',
          links: [
            { label: 'Faux Wood Blinds', href: '/products/motorized-faux-wood-blinds' },
            { label: 'Vertical Blinds', href: '/vertical-blinds', photoOf: 'fabric-vertical-blinds' },
          ],
        },
      ],
      [
        {
          title: 'By Feature',
          links: [
            { label: 'Motorized', href: '/products/motorized-faux-wood-blinds' },
            { label: 'Cordless', href: '/cordless-blinds' },
            { label: 'Child Safe', href: '/child-safe-blinds' },
            { label: 'Energy Saving', href: '/energy-efficient-blinds' },
          ],
        },
      ],
      [
        {
          title: 'By Window',
          links: [
            { label: 'Sliding Glass Doors', href: '/blinds-for-sliding-doors' },
            { label: 'French Doors', href: '/blinds-for-french-doors' },
            { label: 'Bay Windows', href: '/blinds-for-bay-windows' },
            { label: 'Large Windows', href: '/blinds-for-large-windows' },
          ],
        },
      ],
    ],
    shopAll: { label: 'Shop All Blinds', href: '/window-blinds' },
  },
  {
    key: 'blackout',
    label: 'Total Blackout',
    href: '/total-blackout',
    // Product 427 (Premium Blackout Cellular Shades) image.
    image: { src: '/uploads/products/ai-427-de16b21d.webp', alt: 'Blackout cellular shade in a nursery' },
    columns: [
      [
        {
          title: 'By Style',
          href: '/total-blackout',
          links: [
            { label: 'Roller Shades with Side Channels', href: '/blackout-roller-shades-with-side-channels', photoOf: 'blackout-roller-shades-side-channels' },
            { label: 'Blackout Roller Shades', href: '/products/blackout-roller-shades' },
            { label: 'Blackout Cellular Shades', href: '/products/premium-blackout-cellular-shades' },
            { label: 'Blackout Pleated Shades', href: '/products/cordless-blackout-pleated-shades' },
          ],
        },
      ],
      [
        {
          title: 'By Feature',
          links: [
            { label: 'Motorized', href: '/products/motorized-blackout-cellular-shades' },
            { label: 'Room Darkening', href: '/room-darkening-shades' },
            { label: 'Obscura Pro™', href: '/obscura-pro', badge: 'SOON' },
          ],
        },
      ],
      [
        {
          // the reference store's No Drill menu has "By Installation Type" here.
          title: 'Find Your Fit',
          links: [
            { label: 'Will It Work on My Window?', href: '/window-suitability' },
            { label: 'Blackout for Nurseries', href: '/blackout-blinds-for-nursery' },
            { label: 'Light Filtering vs Blackout', href: '/light-filtering-vs-blackout' },
            { label: 'Blackout Windows Guide', href: '/blackout-windows' },
          ],
        },
      ],
    ],
    shopAll: { label: 'Shop All Total Blackout', href: '/total-blackout' },
  },
  {
    key: 'motorized',
    label: 'Motorized',
    href: '/motorized-shades',
    // Product 455 (Motorized Blackout Cellular Shades) image.
    image: { src: '/uploads/products/ai-455-6f21cb5b.webp', alt: 'Motorized cellular shades in a living room' },
    columns: [
      [
        {
          title: 'By Style',
          href: '/motorized-shades',
          links: [
            { label: 'Motorized Roller Shades', href: '/products/motorized-roller-shades' },
            { label: 'Motorized Cellular Shades', href: '/products/motorized-blackout-cellular-shades' },
            { label: 'Motorized Solar Shades', href: '/products/motorized-solar-shades' },
            { label: 'Motorized Faux Wood Blinds', href: '/products/motorized-faux-wood-blinds' },
          ],
        },
      ],
      [
        {
          title: 'By Smart Home',
          href: '/smart-home-compatibility',
          links: [
            { label: 'Amazon Alexa', href: '/smart-blinds-alexa' },
            { label: 'Google Home', href: '/smart-blinds-google-home' },
            { label: 'Apple HomeKit', href: '/smart-blinds-homekit' },
          ],
        },
      ],
      [
        {
          title: 'By Window',
          links: [
            { label: 'High Windows', href: '/motorized-blinds-for-high-windows' },
            { label: 'Large Windows', href: '/motorized-shades-for-large-windows' },
            { label: 'How Motorization Works', href: '/how-motorization-works' },
            { label: 'Motorized vs Cordless', href: '/motorized-vs-cordless-blinds' },
          ],
        },
      ],
    ],
    shopAll: { label: 'Shop All Motorized', href: '/motorized-shades' },
  },
  {
    key: 'skylight',
    label: 'Skylight',
    href: '/cellular-shades-for-skylights',
    // Category 42 (Skylight) image.
    image: { src: '/uploads/categories/categories_1781385859168_6c3d53e4b776b52e.webp', alt: 'Skylight shade with a remote' },
    columns: [
      [
        {
          title: 'By Style',
          links: [
            // Product 464: the frame-and-side-rails blackout blind. The owner
            // classifies "blinds with rails" as skylight blinds (2026-10-17).
            { label: 'Skylight Blinds with Side Rails', href: '/products/complete-blackout-blinds' },
            { label: 'Cellular Skylight Shades', href: '/cellular-shades-for-skylights' },
          ],
        },
      ],
      [
        {
          title: 'Skylight Help',
          links: [
            { label: 'How to Measure a Skylight', href: '/guides/measuring-skylights' },
            { label: 'Skylight Blinds Guide', href: '/blog/skylight-blinds-guide' },
            { label: 'Get a Free Skylight Quote', href: '/consultation' },
          ],
        },
      ],
    ],
    shopAll: { label: 'Shop Skylight Blinds', href: '/products/complete-blackout-blinds' },
  },
  {
    key: 'outdoor',
    label: 'Outdoor',
    href: '/outdoor-roller-shades',
    // Category 15 (Outdoor Shades) image.
    image: { src: '/uploads/categories/categories_1770691651140_1699ef7843c4e1bb.webp', alt: 'Outdoor shades on a pergola' },
    columns: [
      [
        {
          title: 'By Style',
          href: '/outdoor-roller-shades',
          links: [
            { label: 'Outdoor Roller Shades', href: '/products/outdoor-roller-shades' },
            { label: 'Bamboo Outdoor Shades', href: '/products/bamboo-outdoor-shades' },
          ],
        },
      ],
      [
        {
          title: 'By Space',
          links: [
            { label: 'Patios & Pergolas', href: '/outdoor-roller-shades' },
            { label: 'Sunrooms', href: '/sunroom-blinds' },
            { label: 'Garages', href: '/garage-blinds' },
          ],
        },
      ],
    ],
    shopAll: { label: 'Shop All Outdoor', href: '/outdoor-roller-shades' },
  },
];

// ── Help dropdown ("?" in the header) ────────────────────────────────────────
// the reference store: Measuring Instructions, Design Consultancy, Commercial
// Sales, Free Samples, Motorization Guide, Help Center, Contact Us, About Us.
// Ours adds installation, which we offer and they do not.
export const HELP_LINKS: MenuLink[] = [
  { label: 'See It on Your Window', href: '/design-studio' },
  { label: 'Find My Shade', href: '/find-my-shades' },
  { label: 'Measuring Instructions', href: '/guides/measuring' },
  { label: 'Free In-Home Consultation', href: '/consultation' },
  { label: 'Professional Installation', href: '/installation' },
  { label: 'Commercial Sales', href: '/commercial' },
  { label: 'Preview Fabrics Online', href: '/samples' },
  { label: 'Motorization Guide', href: '/how-motorization-works' },
  { label: 'Help Center', href: '/help' },
  { label: 'Track Order', href: '/track-order' },
  { label: 'Contact Us', href: '/contact' },
  { label: 'About Us', href: '/about' },
];

// ── Collection pages: "Explore more …" strip ────────────────────────────────
// the reference store puts a row of photo tiles for the sub-styles under every
// collection page's title ("Explore more Roller Shades": Solar, Zebra, Light
// Filtering, Blackout …). Ours come from the menu panel the page belongs to,
// so a style added to the menu shows up there too. Only the product groups
// become tiles — guides, help and smart-home links do not.
const TILE_GROUP_TITLES = new Set(['Shades', 'Blinds', 'Specialty', 'By Style', 'By Feature']);

/**
 * The menu section a collection page belongs to — the section whose own page
 * it is, else the first type menu that links to it, else All Blinds & Shades —
 * and its product links, minus the page itself.
 */
export function collectionStyleLinks(path: string): { section: MenuSection; links: MenuLink[] } {
  const links = (s: MenuSection) => s.columns.flat().flatMap((g) => g.links);
  const section =
    MENU_SECTIONS.find((s) => s.href === path) ??
    MENU_SECTIONS.find((s) => s.key !== 'all' && links(s).some((l) => l.href === path)) ??
    MENU_SECTIONS[0];
  const tiles = section.columns
    .flat()
    .filter((g) => TILE_GROUP_TITLES.has(g.title))
    .flatMap((g) => g.links)
    .filter((l) => l.href !== path && l.label !== 'Shop All');
  return { section, links: uniqueByHref(tiles) };
}

const group = (sectionKey: string, title: string): MenuLink[] =>
  MENU_SECTIONS.find((s) => s.key === sectionKey)?.columns.flat().find((g) => g.title === title)?.links ?? [];

const uniqueByHref = (links: MenuLink[]): MenuLink[] =>
  links.filter((link, i) => links.findIndex((l) => l.href === link.href) === i);

// ── Footer ───────────────────────────────────────────────────────────────────
// Built from the menu so the footer always repeats it.
export const FOOTER_COLUMNS: MenuGroup[] = [
  {
    title: 'Shop by Type',
    links: uniqueByHref([
      { label: 'All Products', href: '/products' },
      ...group('all', 'Shades'),
      ...group('all', 'Blinds').filter((l) => l.label !== 'Shop All'),
      { label: 'Skylight Blinds', href: '/products/complete-blackout-blinds' },
      { label: 'Outdoor Shades', href: '/outdoor-roller-shades' },
    ]),
  },
  {
    title: 'Shop by Feature',
    links: [
      { label: 'Total Blackout', href: '/total-blackout' },
      ...group('all', 'By Feature'),
      { label: 'Child Safe', href: '/child-safe-blinds' },
    ],
  },
  { title: 'Shop by Room', links: [...ROOM_LINKS, { label: 'All Rooms', href: '/rooms' }] },
  {
    title: 'Installation',
    links: [
      { label: 'Free In-Home Consultation', href: '/consultation' },
      { label: 'Professional Installation', href: '/installation' },
      { label: 'Service Areas', href: '/service-areas' },
      { label: 'Our Work', href: '/gallery' },
      { label: 'Property Managers', href: '/property-managers' },
      { label: 'Perfect Fit Guarantee', href: '/perfect-fit-guarantee' },
    ],
  },
  {
    title: 'Help & Service',
    links: uniqueByHref([
      { label: 'Help Center', href: '/help' },
      { label: 'Contact Us', href: '/contact' },
      { label: 'Shipping Info', href: '/shipping' },
      { label: 'Returns', href: '/returns' },
      { label: 'Track Order', href: '/track-order' },
      { label: 'FAQ', href: '/faq' },
      { label: 'How to Measure', href: '/guides/measuring' },
      { label: 'Installation Guide', href: '/guides/installation' },
      { label: 'See It on Your Window', href: '/design-studio' },
      { label: 'Blog', href: '/blog' },
      { label: 'Energy Savings Calculator', href: '/tools/energy-savings-calculator' },
    ]),
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', href: '/about' },
      { label: 'Our Experts', href: '/our-experts' },
      { label: 'For Vendors', href: '/vendors' },
      { label: 'Warranty', href: '/warranty' },
      { label: 'Price Match Guarantee', href: '/price-match-guarantee' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Do Not Sell My Information', href: '/privacy#ccpa' },
      { label: 'Accessibility', href: '/accessibility' },
    ],
  },
];
